/**
 * Thin client for the LLEO backend data API (Cloud SQL behind FastAPI).
 *
 * Supabase is now used for authentication only. Every piece of application
 * data — analyses, community/likes, conversation messages, credits, billing —
 * goes through these endpoints.
 */
import { GCP_BACKEND_URL, Analysis } from '../types';

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
  has_more: boolean;
}

export interface CommunityPage extends Paginated<Analysis> {
  /** session_ids the requesting user has liked (only when user_id was sent) */
  liked: string[];
}

export interface ConversationMessage {
  id: number;
  conversation_id: string;
  user_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  message_type: string;
  metadata: Record<string, any> | null;
  created_at: string;
}

export interface UserCredits {
  user_id: string;
  credits: number;
  total_used: number;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface SubscriptionInfo {
  user_id: string;
  user_email?: string | null;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  stripe_price_id?: string | null;
  status: string;
  plan_type: string;
  current_period_start?: string | null;
  current_period_end?: string | null;
  cancel_at_period_end: boolean;
}

export interface CreditTransaction {
  id: number;
  user_id: string;
  amount: number;
  transaction_type: string;
  description?: string | null;
  analysis_id?: string | null;
  balance_after: number;
  created_at: string;
}

export interface PaymentRecord {
  id: number;
  user_id: string;
  stripe_payment_intent_id?: string | null;
  stripe_invoice_id?: string | null;
  amount: number;
  currency: string;
  status: string;
  description?: string | null;
  created_at: string;
}

export interface BillingSummary {
  credits: UserCredits;
  subscription: SubscriptionInfo | null;
  transactions: CreditTransaction[];
  payments: PaymentRecord[];
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${GCP_BACKEND_URL}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body?.detail || body?.error || JSON.stringify(body);
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, detail);
  }
  return (await res.json()) as T;
}

const q = (params: Record<string, string | number | undefined | null>) =>
  Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');

// ── analyses ────────────────────────────────────────────────────────────────

export const fetchMyAnalyses = (userId: string, page = 1, perPage = 50) =>
  request<Paginated<Analysis>>(`/api/analyses?${q({ user_id: userId, page, per_page: perPage })}`);

/**
 * One analysis by session_id — used by the deep links
 * `/community/<id>` and `/my-analyses/<id>`. Shared analyses are public;
 * private ones require the owner's user_id (404 otherwise).
 */
export const fetchAnalysis = (sessionId: string, userId?: string | null) =>
  request<Analysis>(`/api/analyses/${encodeURIComponent(sessionId)}?${q({ user_id: userId })}`);

/** Load *all* analyses for a user (follows pagination). */
export async function fetchAllMyAnalyses(userId: string): Promise<Analysis[]> {
  const out: Analysis[] = [];
  let page = 1;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const res = await fetchMyAnalyses(userId, page, 100);
    out.push(...res.items);
    if (!res.has_more) break;
    page += 1;
  }
  return out;
}

export const setAnalysisShared = (sessionId: string, userId: string, isShared: boolean) =>
  request<Analysis>(`/api/analyses/${encodeURIComponent(sessionId)}/share`, {
    method: 'PATCH',
    body: JSON.stringify({ user_id: userId, is_shared: isShared }),
  });

export const setConversationShared = (conversationId: string, userId: string, isShared: boolean) =>
  request<{ conversation_id: string; is_shared: boolean; updated: number }>(
    `/api/conversations/${encodeURIComponent(conversationId)}/share`,
    { method: 'PATCH', body: JSON.stringify({ user_id: userId, is_shared: isShared }) },
  );

export const deleteAnalysis = (sessionId: string, userId: string) =>
  request<{ success: boolean }>(`/results/${encodeURIComponent(sessionId)}?${q({ user_id: userId })}`, {
    method: 'DELETE',
  });

// ── community ───────────────────────────────────────────────────────────────

export const fetchCommunity = (page = 1, perPage = 50, userId?: string | null) =>
  request<CommunityPage>(`/api/community?${q({ page, per_page: perPage, user_id: userId })}`);

export async function fetchAllCommunity(userId?: string | null): Promise<CommunityPage> {
  const items: Analysis[] = [];
  const liked = new Set<string>();
  let page = 1;
  let last: CommunityPage | null = null;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    last = await fetchCommunity(page, 100, userId);
    items.push(...last.items);
    last.liked.forEach(id => liked.add(id));
    if (!last.has_more) break;
    page += 1;
  }
  return { items, total: last?.total ?? items.length, page: 1, per_page: items.length, has_more: false, liked: Array.from(liked) };
}

export interface SharedConversation {
  conversation_id: string;
  analyses: Analysis[];
  messages: ConversationMessage[];
  first_query: string;
  location: string | null;
  user_name: string | null;
  created_at: string | null;
  count: number;
}

/** Public view of a shared conversation: its shared analyses + chat transcript.
    404 when nothing in that conversation is shared. */
export const fetchCommunityConversation = (conversationId: string, userId?: string | null) =>
  request<SharedConversation>(
    `/api/community/conversations/${encodeURIComponent(conversationId)}?${q({ user_id: userId })}`,
  );

export const toggleLike = (sessionId: string, userId: string) =>
  request<{ session_id: string; liked: boolean; likes: number }>(
    `/api/analyses/${encodeURIComponent(sessionId)}/like`,
    { method: 'POST', body: JSON.stringify({ user_id: userId }) },
  );

// ── conversation messages ───────────────────────────────────────────────────

export const fetchConversationMessages = (conversationId: string) =>
  request<{ conversation_id: string; messages: ConversationMessage[] }>(
    `/api/conversations/${encodeURIComponent(conversationId)}/messages`,
  );

// ── credits / billing ───────────────────────────────────────────────────────

export const fetchCredits = (userId: string) => request<UserCredits>(`/api/credits/${encodeURIComponent(userId)}`);

export const fetchBillingSummary = (userId: string) =>
  request<BillingSummary>(`/api/billing/summary/${encodeURIComponent(userId)}`);
